import React from 'react';
import { Star } from '@phosphor-icons/react';
import { Chip, MonatsPips, PersonAvatar } from '../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../lib/immobilien/format';
import { avatarTon, flagText, type DlZeile } from '../../../../lib/dienstleister/liste';
import { DL_STATUS, statusText } from '../../../../lib/dienstleister/status';

/** Bewertungs-Gold (Handoff Dienstleister §4 – einzige zusätzliche Farbe). */
export const GOLD = '#e0a526';
export const GOLD_SOFT = 'rgba(224,165,38,0.15)';

/** Portal noch nicht angebunden (Entscheidung 14.10.2026) – Punkt grau. */
export const PORTAL_PUNKT = { farbe: 'var(--ink3)', label: 'Kein Portal' };

export const objekteText = (z: Pick<DlZeile, 'objektIds'>) =>
  z.objektIds.length === 0 ? 'keine Objekt-Zuordnung' : `${z.objektIds.length} ${z.objektIds.length === 1 ? 'Objekt' : 'Objekte'}`;
export const bewertungText = (b: number | null) => (b === null ? '–' : `${b.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ★`);

export function rechnungAnzeige(z: Pick<DlZeile, 'offen' | 'rechnungen'>): { text: string; farbe: string; flaeche: string } {
  if (z.offen > 0.005) return { text: eur(-z.offen), farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' };
  if (z.rechnungen === 0) return { text: 'Keine Rechnungen', farbe: 'var(--ink3)', flaeche: 'var(--fill)' };
  return { text: 'Alle bezahlt', farbe: 'var(--ok)', flaeche: 'var(--fill)' };
}

export const DienstleisterKachel = React.memo(function DienstleisterKachel({ z, onOeffnen }: { z: DlZeile; onOeffnen: (id: string) => void }) {
  const st = DL_STATUS[z.status];
  const re = rechnungAnzeige(z);
  const tastatur = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(z.id); }
  };
  return (
    <article role="link" tabIndex={0} aria-label={`${z.name} öffnen`} onClick={() => onOeffnen(z.id)} onKeyDown={tastatur}
      className="immo-glas immo-hover-kachel flex flex-col cursor-pointer min-w-0" style={{ borderRadius: 26, padding: 16, gap: 14 }}>
      <div className="flex items-center min-w-0" style={{ gap: 12 }}>
        <PersonAvatar text={initialen(z.name)} ton={avatarTon(z)} punkt={PORTAL_PUNKT} />
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
          <span className="truncate" style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>{z.name}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
            {[z.kategorien.join(', ') || 'Keine Kategorie', objekteText(z)].join(' · ')}
          </span>
        </div>
        <Chip ton={st.ton} punkt>{statusText(z.status, z.vertragEndet)}</Chip>
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <div className="flex flex-col min-w-0" style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: 'var(--fill)' }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Volumen {new Date().getFullYear()}</span>
          <b className="immo-zahl truncate" style={{ fontSize: 15, fontWeight: 800 }}>{eur(z.volumen, { nachkomma: 0 })}</b>
        </div>
        <div className="flex flex-col min-w-0" style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: 'var(--fill)' }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Aufträge</span>
          <b className="immo-zahl" style={{ fontSize: 15, fontWeight: 800 }}>{z.offeneAuftraege} offen</b>
        </div>
        <div className="flex flex-col min-w-0" style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: z.bewertung === null ? 'var(--fill)' : GOLD_SOFT }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Bewertung</span>
          <b className="immo-zahl flex items-center" style={{ fontSize: 15, fontWeight: 800, gap: 3, color: z.bewertung === null ? 'var(--ink3)' : GOLD }}>
            {z.bewertung === null ? '–' : <>{z.bewertung.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}<Star weight="fill" size={13} /></>}
          </b>
        </div>
      </div>
      <div className="flex items-center justify-between" style={{ borderRadius: 16, padding: '10px 12px', gap: 8, background: re.flaeche }}>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Rechnungen</span>
        <b className="immo-zahl" style={{ fontSize: 15, fontWeight: 800, color: re.farbe }}>{re.text}</b>
      </div>
      <div className="flex items-center" style={{ gap: 8 }}>
        <MonatsPips pips={z.pips} />
        <span className="whitespace-nowrap truncate" style={{ fontSize: 11, color: 'var(--ink3)', maxWidth: '55%' }}>{flagText(z)}</span>
      </div>
    </article>
  );
});
